/**
 * annotationUndo — a bounded, inverse-op undo log for segmentation annotation
 * actions (lasso strokes, bump edits, deletions).
 *
 * Ctrl+Z (`useViewerHotkeys` `mod+z`) pops the newest entry and applies its
 * `undo()` closure, so undo always reverts the MOST-RECENT action regardless of
 * which tool produced it — fixing the prior fixed-priority handler that deleted
 * a whole contour on a bump and couldn't restore a deletion.
 *
 * SCOPE KEY — why each entry carries a `{ studyUid, session }` scope.
 * The log is a module global, but its valid lifetime is "one annotation session
 * in the labeling viewer". Rather than make correctness depend on an
 * exhaustive, ever-growing list of exit paths each remembering to `clearUndo()`
 * (cancel, unmount, route-nav, …) — a list that rots — every entry is stamped
 * with the scope active when it was recorded, and `popUndo(cur)` returns only an
 * entry whose scope matches the caller's current scope, discarding stale ones.
 * A fresh `session` is minted at each session entry point (`beginUndoSession`),
 * so a `deleteRestore` closure captured while annotating lesion A can never be
 * popped — and thus re-added and saved onto the wrong study/lesion (`handleSave`
 * matches contours by segmentIndex only) — once lesion B's session has begun.
 * `useViewerHotkeys` additionally passes a null scope whenever segmentation is
 * not active, so a non-empty log is inert between sessions and in the demo
 * viewer (which mounts the same `mod+z` consumer). The `clearUndo()` calls are
 * therefore memory hygiene, not correctness.
 *
 * Deliberately NOT a full-state snapshot history and has NO redo (per the
 * 2026-06-03 design): each entry carries only the closure needed to reverse its
 * own action.
 *
 * UNIFIED across the tools that push here (2026-06-22): lasso strokes
 * (`removeContour`), bump edits (`bumpRevert`), deletes (`deleteRestore`), and
 * nnInteractive seed/refine (`nnRevert`) each push exactly one entry, so `mod+z`
 * is a single `popUndo()` that reverts the most-recent action regardless of which
 * tool produced it — there is no priority cascade. A multi-plane nn seed is ONE
 * action → one entry → one Ctrl+Z. The action-specific inverse (remove created
 * contours, restore a pre-refine snapshot, re-render) lives entirely in the
 * pushed closure, so this module stays pure and tool-agnostic.
 *
 * Segmentation threshold grows also push one `thresholdRevert` entry, including
 * companion contours and propagation. Outside segmentation, region grow keeps
 * its existing modal preview-undo behavior.
 *
 * Pure module — no Cornerstone import (only a logger). Callers build the `undo`
 * closure (capturing whatever they need), so this stays trivially unit-testable.
 */

import { logger } from "../utils/logger";

export type UndoKind =
  | "removeContour"
  | "bumpRevert"
  | "lassoRevert"
  | "deleteRestore"
  | "nnRevert"
  | "thresholdRevert";

/** Identifies the annotation session an undo entry belongs to. `session` is a
 *  monotonic counter minted at each session entry point — deliberately NOT
 *  segmentIndex, which a cancelled-then-restarted lesion reuses. */
export interface UndoScope {
  studyUid: string;
  session: number;
}

export interface UndoEntry {
  /** What kind of action this reverses (debug/telemetry; dispatch is via `undo`). */
  kind: UndoKind;
  /** The session this entry belongs to; stamped by `pushUndo` from the active
   *  scope and checked by `popUndo` so stale entries can never be applied. */
  scope: UndoScope;
  /** Apply the inverse of the action. Must be idempotent-safe (e.g. skip if the
   *  target annotation no longer exists). */
  undo: () => void;
}

/** Cap on retained entries — the "last 20 actions" queue (the depth this log
 *  held before the unify; kept at 20 so folding nn seed/refine into the same
 *  queue doesn't shrink lasso/bump/delete undo depth). Beyond that is not a real
 *  annotation workflow; bounding keeps captured polyline snapshots from
 *  accumulating unboundedly. */
const MAX_ENTRIES = 20;

let log: UndoEntry[] = [];
let sessionCounter = 0;
let activeScope: UndoScope | null = null;

function sameScope(a: UndoScope | null, b: UndoScope | null): boolean {
  return !!a && !!b && a.studyUid === b.studyUid && a.session === b.session;
}

/** Begin a new annotation session (wired at `handleAddNew` / `handleEdit` /
 *  hotkey auto-start). Mints a fresh session id so entries recorded in a prior
 *  (saved or cancelled) session are stale-scoped — `popUndo` for this session
 *  skips them, so a forgotten `clearUndo()` can never re-add a prior session's
 *  contour. Does not clear the log: stale entries are dropped lazily at pop and
 *  bounded by `MAX_ENTRIES`. */
export function beginUndoSession(studyUid: string): void {
  sessionCounter += 1;
  activeScope = { studyUid, session: sessionCounter };
}

/** The scope new entries are stamped with and that `popUndo` matches against —
 *  i.e. the current annotation session, or null when none is active. */
export function getActiveScope(): UndoScope | null {
  return activeScope ? { ...activeScope } : null;
}

/** Record an action's inverse, stamped with the active session scope. Drops the
 *  oldest entry past the cap. */
export function pushUndo(entry: Omit<UndoEntry, "scope">): void {
  if (!activeScope) {
    // No active session: an undo-able action fired outside an annotation
    // session, which is a wiring bug. Stamp an inert scope (session 0 never
    // matches a real session, which starts at 1) and warn rather than silently
    // mis-scoping the entry onto whatever session pops next.
    logger.warn(
      "[annotationUndo] pushUndo with no active session — entry stamped inert:",
      entry.kind,
    );
  }
  log.push({ ...entry, scope: activeScope ?? { studyUid: "", session: 0 } });
  if (log.length > MAX_ENTRIES) log.shift();
}

/** Remove and return the newest entry whose scope matches `cur`, discarding any
 *  stale-scope entries above it. Returns null when `cur` is null (no active
 *  session — e.g. the demo viewer, or between labeling sessions) or no entry
 *  matches. Discarding stale entries is where a forgotten `clearUndo()` stops
 *  mattering. */
export function popUndo(cur: UndoScope | null): UndoEntry | null {
  if (!cur) return null;
  while (log.length) {
    const entry = log.pop() as UndoEntry;
    if (sameScope(entry.scope, cur)) return entry;
    // Stale entry from a prior session / another study — drop it. It can never
    // be a valid undo for the current session.
  }
  return null;
}

/** Drop the whole log and end the active session. Memory hygiene on save /
 *  case change / panel-delete / cancel / unmount — correctness no longer
 *  depends on calling it (see SCOPE KEY above). */
export function clearUndo(): void {
  log = [];
  activeScope = null;
}

/** Kind of the entry the next `popUndo(cur)` would return (without removing it),
 *  or null if none match. Diagnostics/testing only. */
export function peekUndoKind(cur: UndoScope | null): UndoKind | null {
  if (!cur) return null;
  for (let i = log.length - 1; i >= 0; i--) {
    if (sameScope(log[i].scope, cur)) return log[i].kind;
  }
  return null;
}

/** Current number of retained entries (testing/diagnostics). */
export function undoLogSize(): number {
  return log.length;
}
